import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, CheckCircle, XCircle } from '../components/common/Icons';

const FeedbackContext = createContext(null);

const VARIANTS = {
  info: {
    icon: Alert,
    iconClass: 'bg-sky-50 text-sky-500',
    confirmClass: 'bg-sky-500 hover:bg-sky-600',
  },
  success: {
    icon: CheckCircle,
    iconClass: 'bg-emerald-50 text-emerald-600',
    confirmClass: 'bg-emerald-600 hover:bg-emerald-700',
  },
  warning: {
    icon: Alert,
    iconClass: 'bg-amber-50 text-amber-700',
    confirmClass: 'bg-amber-600 hover:bg-amber-700',
  },
  danger: {
    icon: XCircle,
    iconClass: 'bg-red-50 text-red-500',
    confirmClass: 'bg-red-500 hover:bg-red-600',
  },
};

export const FeedbackProvider = ({ children }) => {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState(null);

  const close = useCallback((result) => {
    setDialog((current) => {
      current?.resolve(result);
      return null;
    });
  }, []);

  const confirm = useCallback((options) => (
    new Promise((resolve) => {
      setDialog({
        type: 'confirm',
        variant: options.variant || 'warning',
        title: options.title || t('feedback.confirmTitle'),
        message: options.message || t('feedback.confirmMessage'),
        confirmText: options.confirmText || t('common.confirm'),
        cancelText: options.cancelText || t('common.cancel'),
        resolve,
      });
    })
  ), [t]);

  const notify = useCallback((options) => (
    new Promise((resolve) => {
      setDialog({
        type: 'alert',
        variant: options.variant || 'info',
        title: options.title || t('feedback.noticeTitle'),
        message: options.message || '',
        confirmText: options.confirmText || t('feedback.okButton'),
        resolve,
      });
    })
  ), [t]);

  useEffect(() => {
    const handleRateLimit = () => {
      notify({
        variant: 'warning',
        title: t('feedback.rateLimitTitle'),
        message: t('feedback.rateLimitMessage'),
      });
    };
    window.addEventListener('clarity:rate-limit', handleRateLimit);
    return () => window.removeEventListener('clarity:rate-limit', handleRateLimit);
  }, [notify, t]);

  const value = useMemo(() => ({ confirm, notify }), [confirm, notify]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <FeedbackDialog dialog={dialog} onClose={close} />
    </FeedbackContext.Provider>
  );
};

const FeedbackDialog = ({ dialog, onClose }) => {
  if (!dialog) return null;
  const variant = VARIANTS[dialog.variant] || VARIANTS.info;
  const Icon = variant.icon;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 px-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-5 shadow-2xl">
        <div className="flex items-start gap-4">
          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${variant.iconClass}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-slate-900">{dialog.title}</h2>
            {dialog.message && <p className="mt-2 text-sm leading-6 text-slate-600">{dialog.message}</p>}
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          {dialog.type === 'confirm' && (
            <button
              className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 transition hover:bg-slate-50"
              onClick={() => onClose(false)}
              type="button"
            >
              {dialog.cancelText}
            </button>
          )}
          <button
            className={`inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-bold text-white transition ${variant.confirmClass}`}
            onClick={() => onClose(true)}
            type="button"
          >
            {dialog.confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) throw new Error('useFeedback must be used inside FeedbackProvider');
  return context;
};
